import { TextLink } from '@mm/components';
import { useConnectedPageCRUDActions } from '@mm/state/actions/ConnectedPageCRUDActions';
import { useFacebookSessionCRUDActions } from '@mm/state/actions/FacebookSessionCRUDActions';
import CompanyModel from '@mm/state/model/CompanyModel';
import {
  OpenConnectPagesModalParams,
  useModalActions,
} from '@mm/state/modules/modalsState/actions';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { activeCompanySelector } from '@mm/state/selectors/viewSelectors';
import { ReactComponent as FacebookIcon } from 'assets/facebook.svg';
import classNames from 'classnames';
import React from 'react';
import { useSelector } from 'react-redux';
import FacebookLoginButton from '../../../common/buttons/FacebookLoginButton/FacebookLoginButton';
import './ConnectPageWarning.pcss';

type Props = {
  className?: string;
  warningMessage?: string;
  btnClassName?: string;
  onPageConnectSuccess?: OpenConnectPagesModalParams['onSuccess'];
  showIconWarning?: boolean;
  destinationPageId?: number;
};

export const useFacebookLoginAndConnectButtonOnSuccess = ({
  onPageConnectSuccess = () => {},
  showIconWarning,
  destinationPageId,
}: Exclude<Props, 'className'>) => {
  const activeCompany = useSelector(activeCompanySelector);
  const facebookPage = useSelector(activeFacebookPageSelector);
  const modalActions = useModalActions();
  const facebookSessionCRUDActions = useFacebookSessionCRUDActions();
  const connectedPageCRUDActions = useConnectedPageCRUDActions();

  const websiteFormatted = activeCompany?.website;

  const handleToken = async (facebookToken: string) => {
    await facebookSessionCRUDActions.create({
      facebookToken,
      notFbUser: true,
    });
    if (activeCompany) {
      modalActions.openConnectPagesModal({
        title: `Connect Facebook Page to ${websiteFormatted || 'your website'}`,
        company: activeCompany as CompanyModel,
        canSelectMultiplePages: false,
        destinationPageId,
        onSuccess: page => {
          if (page) {
            connectedPageCRUDActions.retrievePage(page.id);
            onPageConnectSuccess(page);
          }
        },
      });
    }
  };

  const onSuccess = ({ facebookToken }: { facebookToken: string }) => {
    if (
      !facebookPage?.realFacebookPage &&
      facebookPage?.squarePicture &&
      showIconWarning
    ) {
      modalActions.openGeneralConfirmModal({
        headerText: 'Heads up!',
        messageText: (
          <div className="connect-page-warning__business-icon-warning-modal__body">
            <FacebookIcon />
            <div>
              Your business icon will update to match your Facebook page after
              you connect your page.
            </div>
          </div>
        ),
        okButtonText: 'OK',
        onCloseHandler: ok => {
          if (ok) {
            handleToken(facebookToken);
          }
        },
      });
    } else {
      handleToken(facebookToken);
    }
  };

  return onSuccess;
};

export const FacebookLoginAndConnectButton = ({
  className,
  onPageConnectSuccess = () => {},
  showIconWarning,
  destinationPageId,
}: Props) => {
  const onSuccess = useFacebookLoginAndConnectButtonOnSuccess({
    onPageConnectSuccess,
    showIconWarning,
    destinationPageId,
  });

  return (
    <FacebookLoginButton
      className={className}
      label="Connect Facebook Page"
      onSuccess={onSuccess}
      iconSize="sm"
    />
  );
};

export const ConnectPageWarning = ({
  warningMessage = 'Please connect Facebook Page first!',
  className,
  btnClassName,
  onPageConnectSuccess,
}: Props) => {
  const facebookPage = useSelector(activeFacebookPageSelector);

  return (
    <div className={classNames(className, 'connect-page-warning')}>
      {warningMessage && (
        <TextLink className="connect-page-warning__message" sm>
          {warningMessage}
        </TextLink>
      )}

      <FacebookLoginAndConnectButton
        className={btnClassName}
        destinationPageId={facebookPage?.id}
        onPageConnectSuccess={onPageConnectSuccess}
      />
    </div>
  );
};
